/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { formataTelefone, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Loading from '../../../components/Loading';
import PageInfo from '../../../components/PageInfo';

import getValidationErrors from '../../../utils/getValidationErrors';

import HeaderRelatorio from '../HeaderRelatorio';
import getResponseErrors from '../../../utils/getResponseErrors';
import Paginacao from '../../../components/Paginacao';

interface IPrimeiroAgendamentoOnlineReturnDTO {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeStatus: number;
  AgePlano: boolean;
  AgeClinica: number;
  created_at: Date;
  created_atFormatado: string;
  paciente: {
    PacCodigo: number;
    PacNome: string;
    PacOrigem: number;
    PacCelular: string;
    PacEmail: string;
  };
  agenda: { ConCodigo: number; ConNome: string };
  clinica: { CliCodigo: number; CliNome: string };
}

export interface IRelatorio {
  count: number;
  data: IPrimeiroAgendamentoOnlineReturnDTO[];
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
}

interface IPaginacao extends IFormBusca {
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const PacientePrimeiroAtendimento: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.ADMINISTRADOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Exibe os pacientes que realizaram o primeiro atendimento. `;

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<
    IPrimeiroAgendamentoOnlineReturnDTO[]
  >();
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const { dataInicio, dataFim, limite, pagina } = paginacao;

        const dataInicioFormatada = format(dataInicio, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFim, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(dataInicio, 'dd/MM/yyyy')} até ${format(
            dataFim,
            'dd/MM/yyyy',
          )}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        const response = await api.get<IRelatorio>(
          `/relatorios/pacientesprimeiroatendimento?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&limite=${limite}&pagina=${pagina}`,
        );

        setDadosRelatorio(undefined);

        const relatorio = response.data.data.map(rel => {
          return {
            ...rel,
            AgeInicioFormatado: format(
              parseISO(rel.AgeInicio.toString()),
              'dd/MM/yyyy HH:mm',
            ),
            created_atFormatado: format(
              parseISO(rel.created_at.toString()),
              'dd/MM/yyyy HH:mm',
            ),
            paciente: {
              ...rel.paciente,
              PacCelular: formataTelefone(rel.paciente.PacCelular),
            },
          };
        });

        setDadosRelatorio(relatorio);
        setTotalRegistros(response.data.count);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        setPaginacao({
          dataInicio: dataInicioSelecionada,
          dataFim: dataFimSelecionada,
          pagina: 1,
          limite: 200,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Pacientes 1º Atendimento</h2>{' '}
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.ADMINISTRADOR}
            />
          </div>
          {user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={3}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={3}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={3}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`PACIENTES 1º AGENDAMENTO ONLINE - ${
            dataExibe?.dataDiaSemana || ''
          }`}
        />

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <b>Total de {totalRegistros} registros</b>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th>Data Agendamento</th>
                    <th>Data Consulta</th>
                    <th>Paciente</th>
                    <th>Celular</th>
                    <th>Agenda</th>
                    <th>Clinica</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <tr
                        key={`usua-${rel.AgeCodigo}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.created_atFormatado}</td>
                        <td>{rel.AgeInicioFormatado}</td>
                        <td>{rel.paciente.PacNome}</td>
                        <td>{rel.paciente.PacCelular}</td>
                        <td>{rel.agenda.ConNome}</td>
                        <td>{rel.clinica.CliNome}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <Paginacao
                limitePagina={paginacao?.limite || 200}
                paginasVizinhas={2}
                totalRegistros={totalRegistros}
                paginaInicio={paginacao?.pagina}
                handleProximaPagina={handleProximaPagina}
              />
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default PacientePrimeiroAtendimento;
